/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import { useLastSubmittedUserParameters } from "app/hooks"
import { selectUiTables } from "features/results/ResultsSlice"
import React from "react"
import { useSelector } from "react-redux"
import { formatCurrency } from "services/computations/utils"

import FinalValueChart from "./FinalValueChart"

const FinalValues: React.FC = () => {
  const { uiTables } = useSelector(selectUiTables)
  const userParameters = useLastSubmittedUserParameters()

  if (!uiTables?.finalValuesDistribution) {
    return (
      <div className="alert alert-info">No data available. Please calculate results first.</div>
    )
  }

  const { statistics, histogramData, annualConsumptionRateUsed, initialPortfolioDollars } =
    uiTables.finalValuesDistribution

  // Check if we have valid data
  if (histogramData.bins.length === 0) {
    return (
      <div className="alert alert-warning">
        Your filters exclude every historical retirement, so no final values distribution can be
        shown. Widen your filters to see one.
      </div>
    )
  }

  const { mean, median, min, max } = statistics

  return (
    <div className="final-values-container">
      <h4 className="mb-4">Portfolio Final Value Distribution</h4>

      <div className="row mb-4">
        <div className="col-12">
          <FinalValueChart
            bins={histogramData.bins}
            counts={histogramData.counts}
            rawCounts={histogramData.rawCounts}
            binRanges={histogramData.binRanges}
            annualConsumptionRate={annualConsumptionRateUsed}
            annualConsumptionAmount={annualConsumptionRateUsed * initialPortfolioDollars}
            isUserDefinedConsumption={
              userParameters?.initialConsumptionPerAnnumDollars !== undefined
            }
          />
        </div>
      </div>

      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Statistics</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 col-6">
                  <div className="mb-3">
                    <div className="text-muted small">Mean</div>
                    <div className="fw-bold">{formatCurrency(mean)}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="mb-3">
                    <div className="text-muted small">Median</div>
                    <div className="fw-bold">{formatCurrency(median)}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="mb-3">
                    <div className="text-muted small">Minimum</div>
                    <div className="fw-bold">{formatCurrency(min)}</div>
                  </div>
                </div>
                <div className="col-md-3 col-6">
                  <div className="mb-3">
                    <div className="text-muted small">Maximum</div>
                    <div className="fw-bold">{formatCurrency(max)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FinalValues
